import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiMapPin, FiClock, FiCamera, FiNavigation } = FiIcons;

function Heritage() {
  const attractions = [
    {
      name: "Maduwanwela Walawwa",
      distance: "15 km",
      description: "Historic manor house and architectural marvel",
      highlights: ["Traditional Kandyan architecture", "Cultural artifacts", "Historical significance"]
    },
    {
      name: "Panamure Elephant Kraal Site",
      distance: "3 km",
      description: "Historic site of the last elephant kraal in Sri Lanka (1950)",
      highlights: ["Diya Bubula natural spring", "Kraal ruins", "Wildlife history"]
    },
    {
      name: "Waulpane Limestone Caves",
      distance: "8 km", 
      description: "Natural caves with stalactites and bat colonies",
      highlights: ["Cave formations", "Bat watching", "Adventure tourism"]
    },
    {
      name: "Duwili Ella Waterfall",
      distance: "18 km",
      description: "Scenic waterfall near Maduwanwela",
      highlights: ["Natural pool", "Photography", "Nature walks"]
    }
  ];

  return (
    <section id="heritage" className="section-padding">
      <div className="container">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Gateway to <span className="text-gradient">Heritage</span>
          </h2>
          <p className="text-xl text-white/80 max-w-3xl mx-auto">
            Discover the rich cultural tapestry of Sabaragamuwa Province. From ancient manor houses 
            to natural wonders, Kumarana Hotel is your perfect base for exploring the region's treasures.
          </p>
        </motion.div>

        <motion.div
          className="glass-card p-8 md:p-12 mb-12"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-3xl font-bold mb-6">The Panamure Legacy</h3>
              <p className="text-white/90 mb-6">
                Panamure holds a unique place in Sri Lankan history as the site of the last elephant kraal 
                in 1950. The defiance of the legendary "Panamure Eth Raja" and the subsequent public outcry 
                led to fundamental changes in wildlife conservation laws.
              </p>
              <p className="text-white/80 mb-6">
                Today, Kumarana Hotel serves as a custodian of this heritage, offering visitors a gateway 
                to understanding and experiencing the rich cultural landscape of the Sabaragamuwa Province.
              </p>
              <div className="flex items-center gap-3 glass-card p-4 rounded-lg">
                <SafeIcon icon={FiMapPin} className="text-blue-300" />
                <span className="font-semibold">Strategic Location:</span>
                <span className="text-white/80">Gateway to Kolonna Electorate</span>
              </div>
            </div>
            <div className="glass-card p-6">
              <h4 className="text-xl font-semibold mb-4 text-center">Historical Timeline</h4>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-3 h-3 bg-blue-400 rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <div className="font-semibold text-blue-300">1950</div>
                    <div className="text-sm text-white/80">Last elephant kraal held in Panamure</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-3 h-3 bg-blue-400 rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <div className="font-semibold text-blue-300">Post-1950</div>
                    <div className="text-sm text-white/80">Wildlife conservation laws reformed</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-3 h-3 bg-blue-400 rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <div className="font-semibold text-blue-300">Present</div>
                    <div className="text-sm text-white/80">Heritage tourism and cultural preservation</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {attractions.map((attraction, index) => (
            <motion.div
              key={index}
              className="glass-card p-6"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              whileHover={{ scale: 1.02 }}
            >
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-semibold">{attraction.name}</h4>
                <div className="flex items-center gap-1 text-blue-300 text-sm">
                  <SafeIcon icon={FiNavigation} />
                  {attraction.distance}
                </div>
              </div>
              
              <p className="text-white/80 text-sm mb-4">{attraction.description}</p>
              
              <div className="space-y-2">
                {attraction.highlights.map((highlight, idx) => (
                  <div key={idx} className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 bg-green-400 rounded-full mt-2 flex-shrink-0"></span>
                    <span className="text-sm text-white/70">{highlight}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          className="mt-12 text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <div className="glass-card p-8 max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold mb-4">Plan Your Heritage Tour</h3>
            <p className="text-white/80 mb-6">
              Let Kumarana Hotel be your base for exploring the cultural treasures of Sabaragamuwa. 
              We can arrange guided tours and provide detailed information about all nearby attractions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:076-8727112" className="btn-glass">
                <SafeIcon icon={FiClock} />
                Plan Your Visit
              </a>
              <a 
                href="https://maps.google.com/?q=Maduwanwela+Walawwa"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-glass"
              >
                <SafeIcon icon={FiCamera} />
                View Attractions
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default Heritage;